import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from 'react';
import cn from 'classnames';
import { Button, useI18n, ChevronRightIcon } from '@catawiki/cata-style';
import { Cookie } from '@catawiki/runtime-toolkit';
import { COOKIES_MODAL_INVOKED, COOKIE_PREFERENCES_SAVED_EVENT, COOKIES_ROUTE, } from '../constants';
import { isHelpCookiesPage, saveConsentToBQ, saveCookiePreferences, setHasPendingCookieConsentSync, } from '../utils';
import { useIsBrowser } from '../shared/hooks/useIsBrowser';
import * as styles from './CookiesBar.module.scss';
export const CookiesBar = () => {
    const i18n = useI18n();
    const isBrowser = useIsBrowser();
    const [hasRejectedAllCookies, setHasRejectedAllCookies] = useState(false);
    const [hasAcceptedAllCookies, setHasAcceptedAllCookies] = useState(false);
    const [hasMadeCustomChoice, setHasMadeCustomChoice] = useState(() => !!(Cookie.hasMarketingCookiesEnabledCookie() &&
        Cookie.hasAnalyticalCookiesEnabledCookie()));
    const handleAgreeClick = useCallback(() => {
        setHasAcceptedAllCookies(true);
    }, []);
    const handleRejectAllClick = useCallback(() => {
        setHasRejectedAllCookies(true);
    }, []);
    const handleManageCookiesClick = useCallback(() => {
        const invokeModalEvent = new CustomEvent(COOKIES_MODAL_INVOKED);
        window.dispatchEvent(invokeModalEvent);
    }, []);
    const isCookiesPage = isHelpCookiesPage(isBrowser ? window.location.pathname : '');
    const hasGivenConsentInMobile = Cookie.getDontShowCookieBarAgain();
    useEffect(() => {
        const handleCookiePreferencesSaved = () => {
            setHasMadeCustomChoice(true);
        };
        window.addEventListener(COOKIE_PREFERENCES_SAVED_EVENT, handleCookiePreferencesSaved);
        if (Cookie.getUserId() && Cookie.getHasPendingCookieConsentSync()) {
            const enableMarketingCookies = Cookie.getMarketingCookiesEnabled();
            const enableAnalyticalCookies = Cookie.getAnalyticalCookiesEnabled();
            const cta = Cookie.getCookiePreferencesUsedCta();
            saveConsentToBQ({
                marketing: enableMarketingCookies,
                analytical: enableAnalyticalCookies,
                cta,
            }).then(() => setHasPendingCookieConsentSync(false));
        }
        return () => {
            window.removeEventListener(COOKIE_PREFERENCES_SAVED_EVENT, handleCookiePreferencesSaved);
        };
    }, []);
    useEffect(() => {
        if (!hasAcceptedAllCookies) {
            return;
        }
        saveCookiePreferences({
            marketing: true,
            analytical: true,
            cta: 'accept_all',
        });
    }, [hasAcceptedAllCookies]);
    useEffect(() => {
        if (!hasRejectedAllCookies) {
            return;
        }
        saveCookiePreferences({
            marketing: false,
            analytical: false,
            cta: 'reject_all',
        });
    }, [hasRejectedAllCookies]);
    useEffect(() => {
        const isBarHidden = isCookiesPage ||
            hasMadeCustomChoice ||
            hasAcceptedAllCookies ||
            hasGivenConsentInMobile ||
            hasRejectedAllCookies;
        window.document.body.classList[isBarHidden ? 'remove' : 'add']('cookie-bar-is-visible');
    }, [
        isCookiesPage,
        hasMadeCustomChoice,
        hasAcceptedAllCookies,
        hasGivenConsentInMobile,
        hasRejectedAllCookies,
    ]);
    if (isCookiesPage ||
        hasGivenConsentInMobile ||
        hasMadeCustomChoice ||
        hasAcceptedAllCookies ||
        hasRejectedAllCookies ||
        // do not render the jsx for the cookies bar on server side rendering
        // as this can cause issues with hydration error and this does not need to be
        // server side rendered
        !isBrowser) {
        return null;
    }
    return (_jsxs("article", { className: "u-position-absolute u-color-black", children: [_jsx("div", { className: styles.overlay }), _jsxs("aside", { className: cn(styles.container, 'u-flex u-flex--direction-column u-flex--justify-content-center u-flex--align-items-center u-p-b-m u-bgcolor-white'), children: [_jsx("div", { className: cn(styles.decline, 'u-flex u-position-relative u-flex--justify-content-end u-w-full u-p-t-s u-p-b-xs u-p-h-m'), children: _jsx(Button, { className: "gtm-cookie-bar-decline", onClick: handleRejectAllClick, variant: "tertiary", icon: ChevronRightIcon, iconPosition: "end", children: i18n.t('cookies_continue_without_accepting') }) }), _jsxs("div", { className: cn(styles.content, 'u-text-center u-m-h-l'), children: [_jsx("p", { className: "u-m-b-xl u-m-t-xs", dangerouslySetInnerHTML: {
                                    __html: i18n.t('cookies_cookie_banner_v2', {
                                        cookie_policy_link: `
                <a href=${COOKIES_ROUTE} class='c-link primary no-underline'>
                  ${i18n.t('cookie_policy_link')}
                </a>`,
                                    }),
                                } }), _jsxs("footer", { className: cn(styles.footer), children: [_jsx(Button, { id: "cookie_bar_agree_button", className: "u-m-r-s gtm-cookie-bar-agree", onClick: handleAgreeClick, children: i18n.t('cookies_agree') }), _jsx(Button, { className: "u-m-r-s gtm-cookie-bar-manage", onClick: handleManageCookiesClick, variant: "secondary", children: i18n.t('cookies_manage_cookies_button') })] })] })] })] }));
};
//# sourceMappingURL=CookiesBar.js.map